import React, { useState, useRef, ChangeEvent, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'common/redux/core/root.reducer';
import { MessageUnit, DeleteConfirmation } from 'common/components';
import AddUserModal from 'modules/user-management/components/add-edit-user-modal/AddUserModal';
import PermissionList from 'modules/user-management/components/permission-list/PermissionList';
import {
    getPermissionListData,
    getAddUserModalVisibility,
    getPermissionMessageStatus,
    getPermissionEditStatus,
    getPermissionListPagination,
    getCreatePermissionStatus,
    getDeletePermissionStatus,
    getUserPermissionListStatus,
    getUserRoles
} from 'modules/user-management/redux/user.selector';
import { userActions } from 'modules/user-management/redux/user.slice';
import { SEARCH_DEBOUNCE_DELAY, PAGE_SIZE_DEFAULT, PAGE_NUMBER_DEFAULT } from 'common/config';
import { Action, ApiStatus, MessageType } from 'common/enums';
import { convertCamelToPascalCase } from 'common/utils/string';
import useDebounce from 'common/hooks/useDebounce';
import './UserPermissions.scss';
import ConfigurationHeader from 'modules/configuration/components/configuration-header/ConfigurationHeader';
import useUserPermissionAccess from 'modules/user-management/role-access-hooks/useUserPermissionAccess';
import { USER_PERMISSION_TITLE } from 'modules/user-management/config/user.config';
import { UserRoleInfo } from 'modules/admin-roles-management/models/role-management.model';
import { getPermissionListWithFullName } from 'modules/user-management/utils/user-permission.utils';

const UserList = () => {
    const dispatch = useDispatch();
    const { isSetUpDisabled } = useUserPermissionAccess();
    const permissionList: UserRoleInfo[] = useSelector((state: RootState) =>
        getPermissionListData(state)
    );
    const userRoles = useSelector((state: RootState) => getUserRoles(state));
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const modalVisible = useSelector((state: RootState) => getAddUserModalVisibility(state));
    const userDataForDeleting = useSelector((state: RootState) => state.user.dataForDelete);
    const [showMessageUnit, setShowMessageUnit] = useState(false);
    const deletemodalhide = () => {
        setDeleteModalVisible(false);
    };
    const [searchInputValue, setSearchInputValue] = useState<string>('');
    const pageSize = useRef(PAGE_SIZE_DEFAULT);
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<string>('');

    useDebounce(
        () => {
            const apiPayload = {
                pageNumber: PAGE_NUMBER_DEFAULT,
                pageSize: pageSize.current,
                searchText: searchInputValue,
                sortColumn: convertCamelToPascalCase(sortColumn),
                sortOrder: sortOrder.toUpperCase()
            };
            dispatch(userActions.getPermissionList(apiPayload));
        },
        [searchInputValue, modalVisible],
        SEARCH_DEBOUNCE_DELAY
    );

    const updateList = () => {
        const apiPayload = {
            pageNumber: PAGE_NUMBER_DEFAULT,
            pageSize: pageSize.current,
            searchText: searchInputValue,
            sortColumn: sortColumn,
            sortOrder: sortOrder
        };
        const onSuccessfulDelete = (isDeleted: boolean) => {
            if (isDeleted) {
                dispatch(userActions.getUserRoles(userRoles.emailAddress));
                dispatch(userActions.getPermissionList(apiPayload));
                setDeleteModalVisible(false);
                setShowMessageUnit(true);
            }
        };
        dispatch(
            userActions.deletePermission({
                data: userDataForDeleting,
                onSuccess: onSuccessfulDelete
            })
        );
    };
    const handlePermissionListSortChange = (colName: string, order: string) => {
        setSortColumn(colName);
        setSortOrder(order);
        const apiPayload = {
            pageNumber: permissionListPaginationStatus.currentPage,
            pageSize: permissionListPaginationStatus.pageSize,
            searchText: searchInputValue,
            sortColumn: convertCamelToPascalCase(colName),
            sortOrder: order.toUpperCase()
        };
        dispatch(userActions.getPermissionList(apiPayload));
    };
    const handlePermissionListPageChange = (pageNumber: number) => {
        const apiPayload = {
            pageNumber: pageNumber,
            pageSize: pageSize.current,
            searchText: searchInputValue,
            sortColumn: convertCamelToPascalCase(sortColumn),
            sortOrder: sortOrder.toUpperCase()
        };

        dispatch(userActions.getPermissionList(apiPayload));
    };

    const handlePermissionListPageSizeChange = async (pageSizeNew: number) => {
        pageSize.current = pageSizeNew;

        const apiPayload = {
            pageNumber: PAGE_NUMBER_DEFAULT,
            pageSize: pageSizeNew,
            searchText: searchInputValue,
            sortColumn: convertCamelToPascalCase(sortColumn),
            sortOrder: sortOrder.toUpperCase()
        };

        dispatch(userActions.getPermissionList(apiPayload));
    };

    const handlePermissionAction = (action: number, data: UserRoleInfo) => {
        switch (action) {
            case Action.Edit: {
                dispatch({ type: userActions.editPermission.type, payload: data });
                break;
            }
            case Action.Delete: {
                setDeleteModalVisible(true);
                dispatch(userActions.setPermissionDeleteData({ userEmailId: data.emailAddress }));
                break;
            }
            default:
                break;
        }
    };
    const messageStatusforPermissionModule = useSelector((state: RootState) =>
        getPermissionMessageStatus(state)
    );

    const permissionListPaginationStatus = useSelector((state: RootState) =>
        getPermissionListPagination(state)
    );
    const permissionListStatus = useSelector((state: RootState) =>
        getUserPermissionListStatus(state)
    );
    const createPermissionStatus = useSelector((state: RootState) =>
        getCreatePermissionStatus(state)
    );
    const deletePermissionStatus = useSelector((state: RootState) =>
        getDeletePermissionStatus(state)
    );
    const permissionEditStatus = useSelector((state: RootState) => getPermissionEditStatus(state));
    const showLoader = () =>
        permissionListStatus === ApiStatus.LOADING ||
        permissionEditStatus === ApiStatus.LOADING ||
        createPermissionStatus === ApiStatus.LOADING ||
        deletePermissionStatus === ApiStatus.LOADING;

    const getMessageUnitVisibility = () =>
        messageStatusforPermissionModule.type !== MessageType.Default;
    const handleMessageUnitClose = () => {
        dispatch(userActions.resetPermissionMessageStatus());
    };

    const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchInputValue(event.target.value);
    };

    return (
        <>
            <div className="route-container">
                <ConfigurationHeader
                    headerTitle={`${USER_PERMISSION_TITLE} (${permissionListPaginationStatus.totalCount})`}
                    buttonTitle={'Add New'}
                    searchVal={searchInputValue}
                    disableButton={isSetUpDisabled}
                    isButtonVisible={true}
                    onSearchChangeHandler={handleSearchInputChange}
                    permissionList={permissionList}
                    searchPlaceholder="Search with Email"
                    // need to update to open up add new modal
                    onAddNew={() =>
                        dispatch({
                            type: userActions.setAddUserModalVisibility.type,
                            payload: true
                        })
                    }
                />

                <PermissionList
                    isLoading={showLoader()}
                    data={getPermissionListWithFullName(permissionList)}
                    onAction={handlePermissionAction}
                    onSortChange={handlePermissionListSortChange}
                    onPageChange={handlePermissionListPageChange}
                    paginationDetails={permissionListPaginationStatus}
                    onPageSizeChange={handlePermissionListPageSizeChange}
                    pageSize={pageSize.current}
                />
                {
                    <DeleteConfirmation
                        isVisible={deleteModalVisible}
                        onDelete={updateList}
                        onClose={deletemodalhide}
                    />
                }
                {showMessageUnit && (
                    <MessageUnit
                        isVisible={getMessageUnitVisibility()}
                        message={messageStatusforPermissionModule.message}
                        type={messageStatusforPermissionModule.type}
                        onClose={handleMessageUnitClose}
                    />
                )}
                {modalVisible && <AddUserModal />}
            </div>
        </>
    );
};
export default UserList;
